import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { NgIf } from '@angular/common';
import { GuideCheckboxComponent } from './guide-checkbox.component';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { animate, style, transition, trigger } from '@angular/animations';
import {
  GUIDE_HIGHLIGHT_FEATURE,
  GUIDE_MIN_ZOOM,
  GUIDE_PROMO_CODE,
  PROMO_CODE_DISMISSED_KEY,
  USER_CATEGORY_KEY,
  USER_GUIDE_DONE_KEY,
  UserGuideService,
} from './user-guide.service';
import { LayerStoreService } from '../mapbox/services/layer-store.service';
import { SelectedCellService } from '../mapbox/services/selected-cell.service';
import { MapBoxService } from '../mapbox/mapbox.service';
import { BreakpointObserverService } from '../../shared/services/breakpoint-observer.service';
import { UserAccessService } from '../../user/access/user-access.service';
import { SearchGeocoderService } from '../../shared/services/search-geocoder.service';
import { GoogleAnalyticsService } from '../../user/google-analytics.service';
import { AuthenticationService } from '../../user/authentication.service';

export type UserCategory = 'homebuyer' | 'realtor' | 'business' | 'researcher';

interface ChecklistState {
  goalSet: boolean;
  layerExplored: boolean;
  zoomedIn: boolean;
  cellClicked: boolean;
}

@Component({
  selector: 'app-user-guide',
  standalone: true,
  imports: [NgIf, GuideCheckboxComponent],
  templateUrl: './user-guide.component.html',
  styleUrl: './user-guide.component.scss',
  animations: [
    trigger('fadePanel', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(8px)' }),
        animate('250ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
      transition(':leave', [
        animate('350ms ease-out', style({ opacity: 0, transform: 'translateY(8px)' })),
      ]),
    ]),
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(6px)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
    ]),
  ],
})
export class UserGuideComponent implements OnInit, OnDestroy {
  private guideService = inject(UserGuideService);
  private geocoderService = inject(SearchGeocoderService);
  private gaService = inject(GoogleAnalyticsService);
  private layerStore = inject(LayerStoreService);
  private selectedCellService = inject(SelectedCellService);
  private mapboxService = inject(MapBoxService);
  private breakpointService = inject(BreakpointObserverService);
  private accessService = inject(UserAccessService);
  private authService = inject(AuthenticationService);

  private destroy$ = new Subject<void>();

  isVisible = false;
  step: 1 | 2 = 1;
  selected: UserCategory | null = null;
  promoVisible = false;
  promoCopied = false;

  readonly promoCode = GUIDE_PROMO_CODE;

  checklist: ChecklistState = {
    goalSet: false,
    layerExplored: false,
    zoomedIn: false,
    cellClicked: false,
  };

  get allTasksDone(): boolean {
    return Object.values(this.checklist).every(Boolean);
  }

  ngOnInit(): void {
    // TODO: guide not adapted for mobile yet
    if (this.breakpointService.isMobile.value) return;

    // Wait for real user data before deciding — skip the initial null BehaviorSubject
    // emission when a token exists (still loading), but pass through immediately when
    // there is no token (user is definitely unauthorized).
    this.authService.userPersonalData
      .pipe(
        filter(data => data !== null || !this.authService.hasAccessToken()),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.initGuide());
  }

  private initGuide(): void {
    if (this.accessService.hasPaidPlan()) return;

    this.accessService.isPaidPlanActive
      .pipe(filter(Boolean), take(1), takeUntil(this.destroy$))
      .subscribe(() => (this.isVisible = false));

    const guideDone = localStorage.getItem(USER_GUIDE_DONE_KEY);
    const promoDismissed = localStorage.getItem(PROMO_CODE_DISMISSED_KEY);

    if (promoDismissed) return; // nothing to show

    if (guideDone) {
      // Guide already done on a previous visit — show full checklist with promo inline
      this.step = 2;
      this.checklist = { goalSet: true, layerExplored: true, zoomedIn: true, cellClicked: true };
      this.promoVisible = true;
      this.isVisible = true;
      return;
    }

    const saved = localStorage.getItem(USER_CATEGORY_KEY) as UserCategory | null;
    this.isVisible = true;
    if (saved) {
      this.selected = saved;
      this.letsGo();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.guideService.highlightFeature$.next(null);
    try { this.mapboxService.map?.off('zoom', this.onMapZoom); } catch {}
  }

  select(cat: UserCategory): void {
    this.selected = cat;
    localStorage.setItem(USER_CATEGORY_KEY, cat);
  }

  letsGo(): void {
    if (!this.selected) return;
    this.step = 2;
    this.checklist.goalSet = true;
    this.gaService.logGuideUserCategory(this.selected);
    this.guideService.highlightFeature$.next(GUIDE_HIGHLIGHT_FEATURE);
    this.geocoderService.requestFocus$.next();
    this.attachStep2Listeners();
  }

  skip(): void {
    this.select('researcher');
    this.letsGo();
  }

  copyPromo(): void {
    navigator.clipboard.writeText(this.promoCode).then(() => {
      this.promoCopied = true;
      setTimeout(() => (this.promoCopied = false), 2000);
    });
  }

  dismissPromo(): void {
    localStorage.setItem(PROMO_CODE_DISMISSED_KEY, 'true');
    this.isVisible = false;
  }

  private attachStep2Listeners(): void {
    this.layerStore.activeLayer
      .pipe(
        filter((layer) => layer === GUIDE_HIGHLIGHT_FEATURE),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        if (this.checklist.layerExplored) return;
        this.checklist.layerExplored = true;
        this.guideService.highlightFeature$.next(null);
        this.geocoderService.requestFocus$.next();
        this.checkAllDone();
      });

    this.mapboxService.mapReady$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.mapboxService.map.on('zoom', this.onMapZoom);
        this.onMapZoom();
      });

    this.selectedCellService.selectedCellGeoId
      .pipe(
        filter((id) => id !== null),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        if (this.checklist.cellClicked) return;
        this.checklist.cellClicked = true;
        this.checkAllDone();
      });
  }

  private onMapZoom = (): void => {
    try {
      if (this.mapboxService.map.getZoom() >= GUIDE_MIN_ZOOM) {
        this.checklist.zoomedIn = true;
        this.mapboxService.map.off('zoom', this.onMapZoom);
        this.checkAllDone();
      }
    } catch {}
  };

  private checkAllDone(): void {
    if (!this.allTasksDone) return;
    localStorage.setItem(USER_GUIDE_DONE_KEY, 'true');
    // Let the last checkbox tick animate, then reveal the promo
    setTimeout(() => {
      this.promoVisible = true;
    }, 800);
  }
}
